import { create } from 'zustand'
import { Decimal, type D, ZERO } from '../engine/decimal'
import { GLOBAL } from '../config/global'
import { LETTERS, LETTERS_BY_ID, LETTER_ID_BY_CHAR, LETTER_HEAT_BY_CHAR, LETTER_WEIGHT_BY_CHAR } from '../config/letters'
import {
  nextLevelCost,
  totalOutput,
  tapValue,
  distinctOwned,
  ownedLetterChars,
  canCraftWord,
  wordBurst,
  lengthDelta,
  type LetterState,
} from '../engine/formulas'
import {
  newWordEntry,
  progressWord,
  globalMultiplier,
  type WordEntry,
} from '../engine/lexicon'
import {
  newMastery,
  updateMastery,
  isMasteryComplete,
  type LetterMastery,
} from '../engine/mastery'
import { isValidWord, dailyWord } from '../config/dictionary'
import { CHALLENGES, type ChallengeState } from '../config/challenges'
import { loadSave, saveGame, type SaveData, type BoardOperator } from './persist'

export type CraftStatus = 'invalid' | 'locked' | 'burst' | 'nocharge'
export interface CraftResult {
  word: string
  status: CraftStatus
  burst?: string // serialized Decimal of Ink gained (status === 'burst')
  isNew: boolean
  chain: number
  at: number
}

interface GameState {
  ink: D
  letters: LetterState[]
  lastSaved: number
  totalTaps: number
  offlineGain: D | null // set on load if offline progress occurred

  // Wordsmith (M2)
  lexicon: WordEntry[]
  quillCharge: number
  quillRegen: number // seconds toward the next charge
  chainLength: number // current combo chain
  lastBurstAt: number // ms epoch of the last Ink-bearing burst (combo window)
  lastBurstGlobalAt: number // ms epoch for the 3s global lockout
  lastCraft: CraftResult | null

  // Forge Metrics (M2.5)
  edition: number // current prestige count (0 until Milestone 4)
  heat: number
  weight: number
  totalLength: number
  unlockedSentences: number[]

  // Letter Mastery (M3)
  mastery: Record<number, LetterMastery>

  // Prestige & Operators (M4)
  type: number       // Type currency
  peakInk: number    // highest Ink ever reached
  boardSlots: number // max slots = startingSlots + edition
  activeLetterIds: number[]
  operators: BoardOperator[]
  typeface: Record<string, number>
  completedChallenges: number[]

  // derived helpers
  outputPerSec: () => D
  distinct: () => number
  canAfford: (id: number) => boolean
  collectedWords: () => Set<string>
  masteredCount: () => number
  challengeState: () => ChallengeState
  isChallengeComplete: (id: number) => boolean

  // actions
  tap: () => void
  buyLetter: (id: number) => void
  levelUp: (id: number) => void
  capitalizeLetter: (id: number) => void
  craftWord: (raw: string) => void
  addToBoard: (letterId: number) => void
  removeFromBoard: (letterId: number) => void
  publishEdition: () => void
  investTypeface: (branch: string) => void
  tick: (dtSeconds: number) => void
  dismissOffline: () => void
  hardReset: () => void
}

function initialLetters(): LetterState[] {
  return LETTERS.map((l) => ({ id: l.id, level: 0, capital: false }))
}

function applyOffline(save: SaveData): { ink: D; gain: D | null } {
  const ink = new Decimal(save.ink)
  const now = Date.now()
  const elapsedSec = Math.max(0, (now - save.lastSaved) / 1000)
  if (elapsedSec < 5) return { ink, gain: null }
  const cappedSec = Math.min(elapsedSec, GLOBAL.offlineCapHours * 3600)
  const rate = totalOutput(save.letters, save.heat ?? 0, save.activeLetterIds, save.typeface)
  const gain = rate.mul(cappedSec).mul(GLOBAL.offlineRate)
  return { ink: ink.add(gain), gain: gain.gt(0) ? gain : null }
}

type InitialState = Pick<
  GameState,
  | 'ink' | 'letters' | 'lastSaved' | 'totalTaps' | 'offlineGain'
  | 'lexicon' | 'quillCharge' | 'quillRegen'
  | 'chainLength' | 'lastBurstAt' | 'lastBurstGlobalAt' | 'lastCraft'
  | 'edition' | 'heat' | 'weight' | 'totalLength' | 'unlockedSentences'
  | 'mastery'
  | 'type' | 'peakInk' | 'boardSlots' | 'activeLetterIds' | 'operators' | 'typeface'
  | 'completedChallenges'
>

const FRESH_WORDSMITH = {
  lexicon: [] as WordEntry[],
  quillCharge: GLOBAL.quillChargeMax,
  quillRegen: 0,
  chainLength: 0,
  lastBurstAt: 0,
  lastBurstGlobalAt: 0,
  lastCraft: null,
  edition: 0,
  heat: 0,
  weight: 0,
  totalLength: 0,
  unlockedSentences: [] as number[],
  mastery: {} as Record<number, LetterMastery>,
  type: 0,
  peakInk: 0,
  boardSlots: GLOBAL.startingSlots,
  activeLetterIds: [] as number[],
  operators: [] as BoardOperator[],
  typeface: {} as Record<string, number>,
  completedChallenges: [] as number[],
}

function buildInitialState(): InitialState {
  const save = loadSave()
  if (save) {
    const { ink, gain } = applyOffline(save)
    return {
      ink,
      letters: save.letters,
      lastSaved: Date.now(),
      totalTaps: save.totalTaps ?? 0,
      offlineGain: gain,
      ...FRESH_WORDSMITH,
      lexicon: save.lexicon ?? [],
      quillCharge: save.quillCharge ?? GLOBAL.quillChargeMax,
      quillRegen: save.quillRegen ?? 0,
      edition: save.edition ?? 0,
      heat: save.heat ?? 0,
      weight: save.weight ?? 0,
      totalLength: save.totalLength ?? 0,
      unlockedSentences: save.unlockedSentences ?? [],
      mastery: save.mastery ?? {},
      type: save.type ?? 0,
      peakInk: save.peakInk ?? 0,
      boardSlots: save.boardSlots ?? GLOBAL.startingSlots,
      activeLetterIds: save.activeLetterIds ?? [],
      operators: save.operators ?? [],
      typeface: save.typeface ?? {},
      completedChallenges: save.completedChallenges ?? [],
    }
  }
  return {
    ink: ZERO,
    letters: initialLetters(),
    lastSaved: Date.now(),
    totalTaps: 0,
    offlineGain: null,
    ...FRESH_WORDSMITH,
  }
}

export const useGame = create<GameState>((set, get) => ({
  ...buildInitialState(),

  outputPerSec: () => totalOutput(get().letters, get().heat, get().activeLetterIds, get().typeface),
  distinct: () => distinctOwned(get().letters),
  canAfford: (id) => {
    const s = get()
    const ls = s.letters.find((l) => l.id === id)!
    return s.ink.gte(nextLevelCost(LETTERS_BY_ID[id], ls.level))
  },
  collectedWords: () => new Set(get().lexicon.map((e) => e.word)),
  masteredCount: () => get().lexicon.reduce((n, e) => n + (e.mastered ? 1 : 0), 0),
  challengeState: () => {
    const s = get()
    return {
      letters: s.letters.map((l) => ({ id: l.id, level: l.level })),
      heat: s.heat,
      weight: s.weight,
      totalLength: s.totalLength,
      lexiconLen: s.lexicon.length,
      distinct: s.distinct(),
      edition: s.edition,
    }
  },
  isChallengeComplete: (id) => get().completedChallenges.includes(id),

  tap: () =>
    set((s) => ({
      ink: s.ink.add(tapValue(s.letters, s.weight, s.activeLetterIds, s.typeface)),
      totalTaps: s.totalTaps + 1,
    })),

  buyLetter: (id) =>
    set((s) => {
      const ls = s.letters.find((l) => l.id === id)!
      if (ls.level > 0) return s
      const cost = nextLevelCost(LETTERS_BY_ID[id], 0)
      if (s.ink.lt(cost)) return s
      const activeLetterIds = s.activeLetterIds.length < s.boardSlots
        ? [...s.activeLetterIds, id]
        : s.activeLetterIds
      return {
        ink: s.ink.sub(cost),
        letters: s.letters.map((l) => (l.id === id ? { ...l, level: 1 } : l)),
        activeLetterIds,
      }
    }),

  levelUp: (id) =>
    set((s) => {
      const ls = s.letters.find((l) => l.id === id)!
      if (ls.level === 0) return s
      const cost = nextLevelCost(LETTERS_BY_ID[id], ls.level)
      if (s.ink.lt(cost)) return s
      return {
        ink: s.ink.sub(cost),
        letters: s.letters.map((l) =>
          l.id === id ? { ...l, level: l.level + 1 } : l,
        ),
      }
    }),

  capitalizeLetter: (id) =>
    set((s) => {
      const ls = s.letters.find((l) => l.id === id)!
      if (ls.level === 0 || ls.capital) return s
      const m = s.mastery[id]
      if (!m || !isMasteryComplete(m)) return s
      const def = LETTERS_BY_ID[id]
      const cost = nextLevelCost(def, 0).mul(GLOBAL.capRitualCostMultiplier)
      if (s.ink.lt(cost)) return s
      return {
        ink: s.ink.sub(cost),
        letters: s.letters.map((l) =>
          l.id === id ? { ...l, level: 1, capital: true } : l,
        ),
      }
    }),

  craftWord: (raw) =>
    set((s) => {
      const word = raw.trim().toUpperCase()
      const now = Date.now()
      if (!word || !isValidWord(word)) {
        return { lastCraft: { word, status: 'invalid', isNew: false, chain: 0, at: now } }
      }
      const owned = ownedLetterChars(s.letters)
      if (!canCraftWord(word, owned)) {
        return { lastCraft: { word, status: 'locked', isNew: false, chain: 0, at: now } }
      }

      // Collection + progression always happen, burst or not.
      const idx = s.lexicon.findIndex((e) => e.word === word)
      const isNew = idx === -1
      const prev = isNew ? newWordEntry(word) : s.lexicon[idx]
      let entry = progressWord(prev) // preserves prev.lastBurstAt

      const lockoutMs = GLOBAL.burstGlobalLockoutSeconds * 1000
      const perWordMs = GLOBAL.wordCooldownSeconds * 1000
      const canBurst =
        s.quillCharge >= 1 &&
        now - s.lastBurstGlobalAt >= lockoutMs &&
        now - prev.lastBurstAt >= perWordMs

      let ink = s.ink
      let quillCharge = s.quillCharge
      let chainLength = s.chainLength
      let lastBurstAt = s.lastBurstAt
      let lastBurstGlobalAt = s.lastBurstGlobalAt
      let burstStr: string | undefined
      let status: CraftStatus = 'nocharge'

      // Forge metric deltas — computed for every valid craft, applied only on burst.
      let deltaHeat = 0
      let deltaWeight = 0
      const seen = new Set<string>()
      for (const ch of word) {
        if (seen.has(ch)) continue
        seen.add(ch)
        deltaHeat += LETTER_HEAT_BY_CHAR[ch] ?? 0
        deltaWeight += LETTER_WEIGHT_BY_CHAR[ch] ?? 0
      }

      if (canBurst) {
        const withinWindow = now - s.lastBurstAt <= GLOBAL.comboWindowSeconds * 1000
        chainLength = withinWindow ? s.chainLength + 1 : 1

        const collected = new Set(s.lexicon.map((e) => e.word))
        collected.add(word)
        const masteredCount =
          s.lexicon.reduce((n, e) => n + (e.word !== word && e.mastered ? 1 : 0), 0) +
          (entry.mastered ? 1 : 0)

        const burst = wordBurst({
          word,
          outputPerSec: totalOutput(s.letters, s.heat, s.activeLetterIds, s.typeface),
          wordLevel: entry.level,
          chainLength,
          isDaily: word === dailyWord(),
          globalMultiplier: globalMultiplier(collected, masteredCount),
        })
        ink = ink.add(burst)
        quillCharge -= 1
        lastBurstAt = now
        lastBurstGlobalAt = now
        entry = { ...entry, lastBurstAt: now }
        burstStr = burst.toString()
        status = 'burst'
      }

      const lexicon = isNew
        ? [...s.lexicon, entry]
        : s.lexicon.map((e, i) => (i === idx ? entry : e))

      // Letter mastery tracking — update for each unique letter in the word.
      const mastery = { ...s.mastery }
      const timeSinceLastBurstSec = s.lastBurstAt > 0 ? (now - s.lastBurstAt) / 1000 : null
      const letterSeen = new Set<string>()
      for (const ch of word) {
        if (letterSeen.has(ch)) continue
        letterSeen.add(ch)
        const letterId = LETTER_ID_BY_CHAR[ch]
        if (letterId == null) continue
        const current = mastery[letterId] ?? newMastery()
        mastery[letterId] = updateMastery(current, word, chainLength, timeSinceLastBurstSec)
      }

      const heat = Math.max(GLOBAL.heatMin, Math.min(GLOBAL.heatMax, s.heat + deltaHeat))
      const weight = Math.max(GLOBAL.weightMin, Math.min(GLOBAL.weightMax, s.weight + deltaWeight))
      const totalLength = s.totalLength + lengthDelta(word.length)

      return {
        ink,
        lexicon,
        quillCharge,
        chainLength,
        lastBurstAt,
        lastBurstGlobalAt,
        heat,
        weight,
        totalLength,
        mastery,
        lastCraft: { word, status, burst: burstStr, isNew, chain: chainLength, at: now },
      }
    }),

  tick: (dt) =>
    set((s) => {
      const ink = s.ink.add(totalOutput(s.letters, s.heat, s.activeLetterIds, s.typeface).mul(dt))
      const peakInk = Math.max(s.peakInk, ink.toNumber())
      let quillCharge = s.quillCharge
      let quillRegen = s.quillRegen
      if (quillCharge < GLOBAL.quillChargeMax) {
        quillRegen += dt
        while (
          quillRegen >= GLOBAL.quillChargeRegenSeconds &&
          quillCharge < GLOBAL.quillChargeMax
        ) {
          quillRegen -= GLOBAL.quillChargeRegenSeconds
          quillCharge += 1
        }
        if (quillCharge >= GLOBAL.quillChargeMax) quillRegen = 0
      } else {
        quillRegen = 0
      }

      // Ink drying over time.
      const effectiveDecay = GLOBAL.heatDecayBase * (1 + s.edition * GLOBAL.heatDecayEditionScale)
      const heat = Math.max(
        GLOBAL.heatMin,
        Math.min(GLOBAL.heatMax, s.heat * (1 - effectiveDecay * dt)),
      )

      // Challenge check — evaluate predicates, unlock sentences.
      const cState: ChallengeState = {
        letters: s.letters.map((l) => ({ id: l.id, level: l.level })),
        heat, weight: s.weight, totalLength: s.totalLength,
        lexiconLen: s.lexicon.length, distinct: s.distinct(), edition: s.edition,
      }
      const completedChallenges = [...s.completedChallenges]
      let unlockedSentences = s.unlockedSentences
      for (const ch of CHALLENGES) {
        if (completedChallenges.includes(ch.id)) continue
        if (!ch.predicate(cState)) continue
        completedChallenges.push(ch.id)
        if (!unlockedSentences.includes(ch.reward.sentenceIndex)) {
          unlockedSentences = [...unlockedSentences, ch.reward.sentenceIndex]
        }
      }

      return { ink, peakInk, quillCharge, quillRegen, heat, unlockedSentences, completedChallenges }
    }),

  addToBoard: (letterId) =>
    set((s) => {
      if (s.activeLetterIds.includes(letterId)) return s
      if (s.activeLetterIds.length >= s.boardSlots) return s
      return { activeLetterIds: [...s.activeLetterIds, letterId] }
    }),

  removeFromBoard: (letterId) =>
    set((s) => ({
      activeLetterIds: s.activeLetterIds.filter((id) => id !== letterId),
    })),

  publishEdition: () =>
    set((s) => {
      const threshold = GLOBAL.prestigeInkEdition1 *
        Math.pow(GLOBAL.prestigeGrowthPerEdition, s.edition)
      if (s.ink.lt(threshold)) return s
      const typeEarned = Math.floor(Math.sqrt(s.ink.toNumber()) / 25.2)
      const newEdition = s.edition + 1
      const newType = s.type + typeEarned
      const newBoardSlots = GLOBAL.startingSlots + newEdition

      // Keep first N owned letters on the board
      const ownedIds = s.letters
        .filter((l) => l.level > 0)
        .map((l) => l.id)
      const activeLetterIds = ownedIds.slice(0, newBoardSlots)

      return {
        ink: ZERO,
        letters: initialLetters(),
        type: newType,
        edition: newEdition,
        boardSlots: newBoardSlots,
        activeLetterIds,
        operators: [],
        mastery: {},
        heat: 0,
        weight: 0,
        totalLength: 0,
        unlockedSentences: [],
        lastSaved: Date.now(),
        totalTaps: 0,
        offlineGain: null,
        lexicon: s.lexicon, // words stay collected
        quillCharge: GLOBAL.quillChargeMax,
        quillRegen: 0,
        chainLength: 0,
        lastBurstAt: 0,
        lastBurstGlobalAt: 0,
        lastCraft: null,
        peakInk: 0,
        typeface: s.typeface, // permanent upgrades stay
      }
    }),

  investTypeface: (branch) =>
    set((s) => {
      const currentTier = s.typeface[branch] ?? 0
      const cost = Math.floor(
        GLOBAL.typefaceBaseCost * Math.pow(GLOBAL.typefaceCostGrowth, currentTier),
      )
      if (s.type < cost) return s
      return {
        type: s.type - cost,
        typeface: { ...s.typeface, [branch]: currentTier + 1 },
      }
    }),

  dismissOffline: () => set({ offlineGain: null }),

  hardReset: () =>
    set({
      ink: ZERO,
      letters: initialLetters(),
      lastSaved: Date.now(),
      totalTaps: 0,
      offlineGain: null,
      ...FRESH_WORDSMITH,
    }),

}))

// Persist on an interval and before unload.
export function startAutosave() {
  const persist = () => {
    const s = useGame.getState()
    saveGame({
      ink: s.ink.toString(),
      letters: s.letters,
      totalTaps: s.totalTaps,
      lastSaved: Date.now(),
      lexicon: s.lexicon,
      quillCharge: s.quillCharge,
      quillRegen: s.quillRegen,
      edition: s.edition,
      heat: s.heat,
      weight: s.weight,
      totalLength: s.totalLength,
      unlockedSentences: s.unlockedSentences,
      mastery: s.mastery,
      type: s.type,
      peakInk: s.peakInk,
      boardSlots: s.boardSlots,
      activeLetterIds: s.activeLetterIds,
      operators: s.operators,
      typeface: s.typeface,
      completedChallenges: s.completedChallenges,
    })
    useGame.setState({ lastSaved: Date.now() })
  }
  const id = window.setInterval(persist, 10000)
  window.addEventListener('beforeunload', persist)
  return () => {
    window.clearInterval(id)
    window.removeEventListener('beforeunload', persist)
  }
}
